import { useEffect, useState } from 'react';

/**
 * Le type d'écran sur lequel SwissTech est ouvert.
 *
 * <p>L'application était dessinée pour le téléphone et se posait, sur une
 * tablette ou un ordinateur, dans une fenêtre étroite au milieu de l'écran,
 * avec un texte trop petit. Elle reconnaît maintenant l'écran d'elle-même et
 * s'y adapte : téléphone (une colonne, barre du bas), tablette (plein écran,
 * contenu plus large), ordinateur (menu sur le côté, contenu large, texte
 * agrandi). Elle suit la rotation de la tablette et le redimensionnement de
 * la fenêtre.
 */

/** Dès cette largeur, en points d'écran : une tablette. */
export const LARGEUR_TABLETTE = 760;

/** Dès cette largeur : un ordinateur (ou une tablette en paysage très large). */
export const LARGEUR_ORDINATEUR = 1180;

/** Réglage de l'affichage (Réglages → Société → Affichage), dans l'appareil. */
const CLE_VUE = 'st_vue';

/** Événement émis quand ce réglage change, pour que l'écran se réadapte. */
export const EVENEMENT_VUE = 'st-vue';

/**
 * Le type d'écran pour une largeur et un réglage.
 *
 * <p>« Auto » suit la largeur. Un choix forcé s'applique sur un écran assez
 * grand pour lui ; un téléphone reste un téléphone, quel que soit le réglage.
 *
 * @param {number} largeur largeur de la fenêtre, en points
 * @param {string} [vue] réglage : auto, telephone, tablette ou ordi
 * @returns {'telephone'|'tablette'|'ordinateur'} le type d'écran
 */
export function typeEcran(largeur, vue = 'auto') {
  if (largeur < LARGEUR_TABLETTE) return 'telephone';
  if (vue === 'telephone') return 'telephone';
  if (vue === 'tablette') return 'tablette';
  if (vue === 'ordi') return 'ordinateur';
  return largeur >= LARGEUR_ORDINATEUR ? 'ordinateur' : 'tablette';
}

function vueEnregistree() {
  try {
    return localStorage.getItem(CLE_VUE) || 'auto';
  } catch (e) {
    return 'auto';
  }
}

/**
 * Le type d'écran courant, mis à jour à chaque changement de taille, de
 * rotation ou de réglage.
 *
 * @returns {'telephone'|'tablette'|'ordinateur'} le type d'écran
 */
export function useEcran() {
  const lire = () =>
    typeEcran(typeof window === 'undefined' ? 480 : window.innerWidth, vueEnregistree());
  const [ecran, setEcran] = useState(lire);
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const maj = () => setEcran(lire());
    window.addEventListener('resize', maj);
    window.addEventListener('orientationchange', maj);
    window.addEventListener(EVENEMENT_VUE, maj);
    return () => {
      window.removeEventListener('resize', maj);
      window.removeEventListener('orientationchange', maj);
      window.removeEventListener(EVENEMENT_VUE, maj);
    };
  }, []);
  return ecran;
}
